import { Map } from 'features/mapping/components/Map';
import { AlertCamerasLayer } from 'features/mapping/components/layers/AlertCamerasLayer';
import { isMediaAlertCamera } from 'components/MediaForm';
import { WildfiresLayer } from 'features/mapping/components/layers/WildfiresLayer';
import {
  StructuredEvacuationsEditLayer,
  STRUCTURED_EVAC_EDIT_LAYER_DEFAULT_PROPS,
  STRUCTURED_EVAC_PENDING_LAYER_DEFAULT_PROPS,
} from 'features/mapping/components/layers/StructuredEvacuationsLayer';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { MapType } from 'features/mapping/mapTypePolicy';
import { ReactNode, useCallback, useMemo } from 'react';
import { LayerEvacZone, FormEvacZoneStatus, LatLng } from 'shared/types';
import { useFormContext } from 'react-hook-form';
import { useInitialMapZoom } from 'hooks/useInitialMapZoom';
import { useLatLngFromQueryParams } from 'hooks/useLatLngFromQueryParams';
import { useMapState } from 'state/useMapState';
import { IncidentMapStateUpdate } from 'features/mapping/mapping.types';
import { useHandleEvacZoneClick } from 'features/evacZones/hooks/useHandleEvacZoneClick';
import { useRegionEvacZones } from 'features/evacZones/hooks/useRegionEvacZones';
import { REGION_EVAC_ZONES_REFETCH_INTERVAL } from 'features/evacZones/constants';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import {
  ActiveFloodingsLayer,
  InactiveFloodingsLayer,
} from 'features/mapping/components/layers/FloodingsLayer';
import { HazardsLayer } from 'features/mapping/components/layers/HazardsLayer';
import { useDefaultMapCenter } from 'hooks/useDefaultMapCenter';
import { FormExternalFeatureLayers } from 'features/mapping/components/layers/FormExternalFeatureLayers';
import {
  NOOP_MARKER_EDIT_LOCK,
  UseMarkerEditLockResult,
} from 'features/geoEvents/hooks/useMarkerEditLock';
import {
  getWildfireEventScale,
  getWildfireGeoEventIcon,
} from 'features/mapping/icons';
import { iconFootprint } from 'features/mapping/components/MarkerEditConnectorLine';
import { MarkerEditControls } from 'features/mapping/components/MarkerEditControls';
import { MarkerEditGhost } from 'features/mapping/components/MarkerEditGhost';
import { WildfireFormValues } from '../../WildfireForm.types';
import { getWildfireRegionsForEvacZones } from '../../WildfireForm.utils';
import { WildfireMarker } from '../WildfireMarker';

type WildfireMapProps = {
  center?: LatLng;
  children?: ReactNode;
  wildfire?: Wildfire;
  isEvacZonesLayerVisible: boolean;
  evacZones: LayerEvacZone[];
  pendingEvacZones: LayerEvacZone[];
  markerLock?: UseMarkerEditLockResult;
  disableStructuredEvacZoneLayerClick?: boolean;
  showHazards?: boolean;
  /**
   * Initial zoom for the map. When provided, it takes precedence over the zoom
   * derived from query params and the main map's last zoom.
   */
  zoom?: number;
  /**
   * Optional callback invoked on every viewport change, in addition to the
   * map's internal bounds tracking. Allows callers persist the form map's
   * center/zoom/bounds (e.g. back into the main map's shared state).
   */
  onViewportChange?: (viewport: IncidentMapStateUpdate) => void;
};

export const WildfireFormMap = (props: WildfireMapProps): JSX.Element => {
  const {
    center,
    children,
    wildfire,
    isEvacZonesLayerVisible,
    evacZones,
    pendingEvacZones,
    markerLock = NOOP_MARKER_EDIT_LOCK,
    disableStructuredEvacZoneLayerClick,
    showHazards = false,
    zoom: zoomProp,
    onViewportChange: onViewportChangeProp,
  } = props;
  const { setValue, watch } = useFormContext<WildfireFormValues>();
  const { setActiveMapBounds } = useMapState();
  const { wildfireEvents, floodingEvents, hazards } = useAllGeoEvents();
  const [regions] = watch(['regions']);
  const { regionEvacZones } = useRegionEvacZones({
    regions: getWildfireRegionsForEvacZones(regions, wildfire),
    evacZoneStatuses: wildfire?.evacZoneStatuses,
    refetchInterval: wildfire ? REGION_EVAC_ZONES_REFETCH_INTERVAL : undefined,
  });

  const [
    name,
    address,
    acreage,
    containment,
    isComplexParent,
    isPrescribed,
    isFps,
    notificationType,
    status,
    lat,
    lng,
    evacZoneStatuses,
    temporarilyDisplayEvacZones,
    media,
    manualDeactivationStarted,
  ] = watch([
    'name',
    'address',
    'acreage',
    'containment',
    'isComplexParent',
    'isPrescribed',
    'isFps',
    'notificationType',
    'status',
    'lat',
    'lng',
    'evacZoneStatuses',
    'temporarilyDisplayEvacZones',
    'media',
    'manualDeactivationStarted',
  ]);

  const onViewportChange = useCallback(
    (viewport: IncidentMapStateUpdate) => {
      setActiveMapBounds(viewport.bounds);
      onViewportChangeProp?.(viewport);
    },
    [setActiveMapBounds, onViewportChangeProp],
  );

  const setFormValue = useCallback(
    (fieldName: string, value: FormEvacZoneStatus[]) => {
      // We need to cast here since setValue is strongly typed
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue(fieldName as keyof WildfireFormValues, value as any);
    },
    [setValue],
  );

  const handleEvacZoneClick = useHandleEvacZoneClick({
    regionEvacZones,
    evacZoneStatuses,
    geoEvent: wildfire,
    setValue: setFormValue,
  });

  const { lat: initialLatitude, lng: initialLongitude } =
    useLatLngFromQueryParams();
  const initialZoom = useInitialMapZoom(zoomProp);

  const defaultMapCenter = useDefaultMapCenter({
    geoEvent: center ?? wildfire,
    initialLat: initialLatitude,
    initialLng: initialLongitude,
  });

  const isActive = status === 'active';

  const displayLat = markerLock.stagedLatLng?.lat ?? lat;
  const displayLng = markerLock.stagedLatLng?.lng ?? lng;

  // While the marker is being actively edited, clicks anywhere on the map reposition the marker
  const isMarkerEditActive = markerLock.enabled && !markerLock.isLocked;

  const disableEvacZoneClick =
    temporarilyDisplayEvacZones ||
    disableStructuredEvacZoneLayerClick ||
    isMarkerEditActive;

  const evacZoneClick = disableEvacZoneClick ? undefined : handleEvacZoneClick;

  const currentGeoEvent = useMemo(() => {
    const geoEventWithFormValues: Partial<Wildfire> = wildfire || {};
    return {
      ...geoEventWithFormValues,
      name,
      address,
      data: {
        ...(geoEventWithFormValues.data || {}),
        acreage,
        containment,
        isComplexParent,
        isFps,
        isPrescribed,
        manualDeactivationStarted,
      },
      notificationType,
      isActive,
      lat: displayLat,
      lng: displayLng,
    } as Partial<Wildfire>;
  }, [
    acreage,
    address,
    containment,
    wildfire,
    isActive,
    isComplexParent,
    isFps,
    isPrescribed,
    displayLat,
    displayLng,
    name,
    notificationType,
    manualDeactivationStarted,
  ]);

  return (
    <Map
      center={defaultMapCenter}
      mapType={MapType.IncidentForm}
      showSearch
      searchDisabled={markerLock.isLocked}
      searchBarAdornment={<MarkerEditControls lock={markerLock} />}
      cursor={markerLock.isLocked ? undefined : 'crosshair'}
      onLocationChange={markerLock.handleLocationChange}
      searchApi="google"
      zoom={initialZoom}
      onViewportChange={onViewportChange}
    >
      <FormExternalFeatureLayers />
      <StructuredEvacuationsEditLayer
        {...STRUCTURED_EVAC_EDIT_LAYER_DEFAULT_PROPS}
        visible={isEvacZonesLayerVisible}
        evacZones={evacZones}
        onClick={evacZoneClick}
      />
      <StructuredEvacuationsEditLayer
        {...STRUCTURED_EVAC_PENDING_LAYER_DEFAULT_PROPS}
        visible={isEvacZonesLayerVisible}
        evacZones={pendingEvacZones}
        onClick={evacZoneClick}
      />
      <AlertCamerasLayer
        visible
        interactive={false}
        withLabels
        selectedCameraId={isMediaAlertCamera(media) ? media.id : undefined}
      />
      {showHazards && <HazardsLayer hazards={hazards} interactive={false} />}
      <InactiveFloodingsLayer
        floodingEvents={floodingEvents}
        interactive={false}
      />
      <WildfiresLayer
        isFadable
        wildfires={wildfireEvents}
        selectedGeoEventId={wildfire?.id}
        interactive={false}
        isEdit
      />
      <ActiveFloodingsLayer
        floodingEvents={floodingEvents}
        interactive={false}
      />
      <WildfireMarker
        geoEvent={currentGeoEvent}
        onChange={markerLock.handleLocationChange}
        locked={markerLock.isLocked}
        moveOnEvacZoneClick={isMarkerEditActive}
      />
      {wildfire && (
        <MarkerEditGhost
          markerLock={markerLock}
          original={wildfire}
          footprint={iconFootprint(
            getWildfireGeoEventIcon(wildfire),
            wildfire.data?.isComplexParent ? 'center' : 'bottom',
            getWildfireEventScale(wildfire),
          )}
        >
          <Map.WildFireGeoEventMarker
            geoEvent={wildfire}
            draggable={false}
            opacityOverride={0.5}
            anchor={wildfire.data?.isComplexParent ? 'center' : 'bottom'}
          />
        </MarkerEditGhost>
      )}
      <Map.MapEvents onViewportChange={onViewportChange} />
      {children}
    </Map>
  );
};
